import type { AuditEntry } from '../../shared/types';
import { fmtAgo, fmtBytes, useApi } from '../lib/hooks';
import { Link } from '../lib/router';
import { Alert, Badge, ErrorBox, Loading, Panel, Stat, StatusBadge } from '../components/ui';
import { describeAction } from './Logs';

export function AdminOverview() {
  const { data, error, loading, reload } = useApi<any>('/api/admin/overview');
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox error={error} retry={reload} />;
  if (!data) return null;
  const jobs = data.jobs ?? {};
  return (
    <div className="stack">
      {data.discordMode === 'fake' && <Alert kind="warn">Running against a <b>fake Discord server</b> (development mode). Set the Discord secrets and DISCORD_MODE=live to connect the real bot.</Alert>}
      {data.maintenance?.active && <Alert kind="warn">Maintenance mode: a restore or purge of <b>{data.maintenance.scope}</b> is running.</Alert>}
      <div className="stats">
        <Stat icon="users" label="Members in Discord" value={data.users.inGuild} hint={`${data.users.active} active accounts · ${data.users.total} total`} />
        <Stat icon="roles" label="Roles" value={data.roles.discord + data.roles.local} hint={`${data.roles.discord} Discord · ${data.roles.local} local`} />
        <Stat icon="tool" label="Tools installed" value={data.tools} hint={`Platform v${data.platformVersion}`} />
        <Stat icon="backup" label="Latest backup" value={data.backups.latest ? fmtAgo(data.backups.latest.createdAt) : 'none'}
          hint={data.backups.latest ? `${data.backups.latest.label} · ${fmtBytes(data.backups.latest.sizeBytes)}` : 'Create one on Backup & Restore'} />
      </div>
      <div className="grid-2">
        <Panel title="Scheduled jobs" icon="refresh">
          <table className="table compact">
            <thead><tr><th>Job</th><th>Last run</th><th>Result</th></tr></thead>
            <tbody>
              {[['sync', 'Discord role & member sync'], ['feeds', 'Channel feeds'], ['backup', 'Automatic backup']].map(([k, label]) => (
                <tr key={k}>
                  <td>{label}</td>
                  <td>{fmtAgo(jobs[k]?.lastRunAt)}</td>
                  <td>{jobs[k]?.lastResult ? <StatusBadge status={jobs[k].lastResult === 'ok' ? 'ok' : 'failed'} /> : <Badge>not yet</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted small">Guild: {data.guildName || data.guildId || 'not configured'}. Jobs are triggered every 5 minutes by Cloud Scheduler.</p>
        </Panel>
        <Panel title="Recent activity" icon="log" actions={<Link to="/admin/logs" className="btn btn-ghost">View log</Link>}>
          <ul className="activity">
            {data.recent.map((e: AuditEntry) => (
              <li key={e.id}>
                <span className={`dot dot-${e.outcome}`} />
                <div><strong>{describeAction(e)}</strong><span className="muted small">{e.actorName} · {fmtAgo(e.at)}</span></div>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}
